import { createRouter, createWebHistory } from 'vue-router'

import AppShell from '@/layouts/AppShell.vue'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guestOnly?: boolean
    title?: string
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: () => ({ top: 0 }),
  routes: [
    { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/auth/ForgotPasswordView.vue'), meta: { guestOnly: true, title: '找回密碼' } },
    { path: '/change-password', name: 'change-password', component: () => import('@/views/auth/ChangePasswordView.vue'), meta: { requiresAuth: true, title: '修改密碼' } },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { guestOnly: true, title: '登入' },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/auth/RegisterView.vue'),
      meta: { guestOnly: true, title: '註冊' },
    },
    {
      path: '/',
      component: AppShell,
      children: [
        { path: '', redirect: '/market' },
        { path: 'announcements', name: 'announcements', component: () => import('@/views/AnnouncementsView.vue'), meta: { title: '公告中心' } },
        { path: 'announcements/:id', name: 'announcement-detail', component: () => import('@/views/AnnouncementsView.vue'), meta: { title: '公告詳情' } },
        {
          path: 'market',
          name: 'market',
          component: () => import('@/views/market/MarketView.vue'),
          meta: { title: '行情' },
        },
        {
          path: 'trade/:symbol?',
          name: 'trade',
          component: () => import('@/views/market/TradeView.vue'),
          meta: { title: '交易' },
        },
        {
          path: 'portfolio',
          name: 'portfolio',
          component: () => import('@/views/market/PortfolioView.vue'),
          meta: { requiresAuth: true, title: '資產' },
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import('@/views/market/OrdersView.vue'),
          meta: { requiresAuth: true, title: '訂單' },
        },
        {
          path: 'user-center',
          name: 'user-center',
          component: () => import('@/views/UserCenterView.vue'),
          meta: { title: '用戶中心' },
        },
        {
          path: 'identity-verification',
          name: 'identity-verification',
          component: () => import('@/views/IdentityVerificationView.vue'),
          meta: { requiresAuth: true, title: '實名驗證' },
        },
        {
          path: 'bank-account',
          name: 'bank-account',
          component: () => import('@/views/BankAccountView.vue'),
          meta: { requiresAuth: true, title: '我的銀行卡' },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/views/ProfileView.vue'),
          meta: { requiresAuth: true, title: '個人資料' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { title: '頁面不存在' },
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.hydrate()

  if (auth.isAuthenticated && auth.user?.passwordChangeRequired && to.name !== 'change-password') {
    return { name: 'change-password' }
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.meta.guestOnly && auth.isAuthenticated) return { name: 'market' }
  document.title = to.meta.title ? `${to.meta.title} · 區塊鏈交易平台` : '區塊鏈交易平台'
  return true
})

window.addEventListener('auth:unauthorized', () => {
  const auth = useAuthStore()
  auth.clear()
  if (router.currentRoute.value.meta.requiresAuth) void router.replace({ name: 'login' })
})

export default router
window.addEventListener('auth:password-change-required', () => { void router.replace({ name: 'change-password' }) })

